import React from 'react';
import { styled } from '@linaria/react';
import { HelpCircle, X } from 'lucide-react';

import { BREAKPOINTS } from '@/constants';
import { range, throttle } from '@/utils';
import useSound from '@/hooks/use-sound';

import Space from '@/components/Space';
import CodeSnippetProvider from '@/components/CodeSnippet/CodeSnippetProvider';
import IconSpeaker from '@/components/Icons/IconSpeaker';

import BaseItem from './Item';
import HelpScreen from './AssignableChildrenHelpScreen';
import { BaseCodeWrapper } from './Grid.shared';
import {
  NUM_OF_ROWS,
  NUM_OF_COLS,
  constructSnippet,
} from './AssignableChildren.helpers';

function AssignableChildren({ codeOutput }) {
  const id = React.useId();
  const [gridRow, setGridRow] = React.useState(null);
  const [gridCol, setGridCol] = React.useState(null);
  const [isPressed, setIsPressed] = React.useState(false);

  const [showHelpScreen, setShowHelpScreen] = React.useState(false);

  const initialPointerPosition = React.useRef(null);

  const gridRowRef = React.useRef(gridRow);
  const gridColRef = React.useRef(gridCol);

  gridRowRef.current = gridRow;
  gridColRef.current = gridCol;

  const [playHover, { stop: stopHover }] = useSound(
    '/sounds/switch-on.wav',
    { volume: 0.1 }
  );
  const [playStart] = useSound('/sounds/plunger-immediate.mp3', {
    volume: 0.2,
  });
  const [playCommit] = useSound('/sounds/pop.mp3', { volume: 0.4 });
  const [playShowHelp] = useSound('/sounds/menu-open.mp3', {
    volume: 0.6,
  });
  const [playEmpty] = useSound('/sounds/empty-trash.wav', {
    volume: 0.4,
  });

  const codeSnippet = constructSnippet({ gridRow, gridCol });

  const hasChild = gridRow !== null && gridCol !== null;

  const handleCommit = React.useCallback(() => {
    setIsPressed(false);
    playCommit();

    window.removeEventListener('pointerup', handleCommit);
  }, [playCommit]);

  function handlePointerDown(rowNum, colNum) {
    setGridRow(String(rowNum));
    setGridCol(String(colNum));
    setIsPressed(true);

    initialPointerPosition.current = {
      rowNum,
      colNum,
    };

    window.addEventListener('pointerup', handleCommit);
  }

  const handleSelectGridCell = React.useCallback(
    function handleSelectGridCell(rowNum, colNum) {
      if (!isPressed || !initialPointerPosition.current) {
        return;
      }

      const rowNums = [
        initialPointerPosition.current.rowNum,
        rowNum,
      ].sort();
      const colNums = [
        initialPointerPosition.current.colNum,
        colNum,
      ].sort();

      // Remove duplicates
      if (rowNums[0] === rowNums[1]) {
        rowNums.pop();
      }
      if (colNums[0] === colNums[1]) {
        colNums.pop();
      }

      // Suppose we're spanning the first two columns. Because CSS
      // Grid is based on line nums, this would need to be written
      // as "1 / 3", not "1 / 2". So, we need to increment the 2nd
      // values:
      if (rowNums[1]) {
        rowNums[1] += 1;
      }
      if (colNums[1]) {
        colNums[1] += 1;
      }

      const nextGridRow = rowNums.join(' / ');
      const nextGridCol = colNums.join(' / ');

      let sfxNeeded = false;

      if (nextGridRow !== gridRowRef.current) {
        setGridRow(nextGridRow);
        sfxNeeded = true;
      }
      if (nextGridCol !== gridColRef.current) {
        setGridCol(nextGridCol);
        sfxNeeded = true;
      }

      if (sfxNeeded) {
        stopHover();
        playHover();
      }
    },
    [isPressed, playHover, stopHover]
  );

  // React doesn't like that I'm not defining the function inline,
  // but it's the simplest solution I can think of.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTouchEvent = React.useCallback(
    throttle(({ clientX, clientY }) => {
      const element = document.elementFromPoint(clientX, clientY);

      const rowNum = element.getAttribute('data-row');
      const colNum = element.getAttribute('data-col');

      if (
        typeof rowNum === undefined ||
        typeof colNum === undefined
      ) {
        return;
      }

      handleSelectGridCell(Number(rowNum), Number(colNum));
    }, 50),
    [handleSelectGridCell]
  );

  function handleKeyDown(event, rowNum, colNum) {
    if (event.code === 'Enter') {
      const nextIsPressed = !isPressed;

      if (nextIsPressed) {
        initialPointerPosition.current = { rowNum, colNum };
        playStart();

        setGridRow(rowNum);
        setGridCol(colNum);
      } else {
        initialPointerPosition.current = null;
        playCommit();
      }

      setIsPressed(nextIsPressed);

      return;
    }

    // For now, focus next button.
    let nextRowNum = rowNum;
    let nextColNum = colNum;

    switch (event.code) {
      case 'ArrowLeft': {
        event.preventDefault();
        nextColNum -= 1;

        if (nextColNum < 1) {
          nextRowNum -= 1;
          nextColNum = NUM_OF_COLS;
        }
        if (nextRowNum < 1) {
          nextRowNum = NUM_OF_ROWS;
        }
        break;
      }

      case 'ArrowRight': {
        event.preventDefault();
        nextColNum += 1;

        if (nextColNum > NUM_OF_COLS) {
          nextRowNum += 1;
          nextColNum = 1;
        }
        if (nextRowNum > NUM_OF_ROWS) {
          nextRowNum = 1;
        }
        break;
      }

      case 'ArrowUp': {
        event.preventDefault();
        nextRowNum -= 1;

        if (nextRowNum < 1) {
          nextRowNum = NUM_OF_ROWS;
        }
        break;
      }

      case 'ArrowDown': {
        event.preventDefault();
        nextRowNum += 1;

        if (nextRowNum > NUM_OF_ROWS) {
          nextRowNum = 1;
        }
        break;
      }
    }

    const nextButtonId = getButtonId(id, nextRowNum, nextColNum);
    document.querySelector('#' + nextButtonId).focus();

    if (isPressed) {
      handleSelectGridCell(nextRowNum, nextColNum);
    }
  }

  const handleDismissHelpScreen = React.useCallback(() => {
    setShowHelpScreen(false);
  }, []);

  return (
    <OuterWrapper as="section">
      <Header>
        <IconSpeaker
          as={SquareButton}
          style={{
            opacity: showHelpScreen ? 0.5 : undefined,
          }}
        />
        <SquareButton
          aria-label="View instructions"
          onClick={() => {
            if (!showHelpScreen) {
              playShowHelp();
            }
            setShowHelpScreen(!showHelpScreen);
          }}
        >
          <HelpCircle size={24} />
        </SquareButton>
        <LongButton
          style={{
            opacity: showHelpScreen ? 0.5 : undefined,
          }}
          onClick={() => {
            setGridRow(null);
            setGridCol(null);
            playEmpty();
          }}
        >
          <X />
          Reset
        </LongButton>
      </Header>

      <Row style={{ opacity: showHelpScreen ? 0 : 1 }}>
        <CodeWrapper>
          <CodeSnippetProvider code={codeSnippet} lang="css">
            {codeOutput}
          </CodeSnippetProvider>
        </CodeWrapper>
        <DemoWrapper>
          <InteractiveLayer>
            <InteractiveWrapper>
              <InteractiveGrid>
                {range(1, NUM_OF_ROWS + 1).map((rowNum) =>
                  range(1, NUM_OF_COLS + 1).map((colNum) => {
                    // Only allow focus for the very first button
                    // (we'll use arrow keys to focus the others),
                    // while the “Help” screen isn't visible.
                    const allowTabs =
                      !showHelpScreen && rowNum === 1 && colNum === 1;

                    return (
                      <GridButton
                        key={`${rowNum}-${colNum}`}
                        id={getButtonId(id, rowNum, colNum)}
                        data-row={rowNum}
                        data-col={colNum}
                        tabIndex={allowTabs ? undefined : '-1'}
                        style={{
                          cursor: isPressed ? 'crosshair' : 'pointer',
                        }}
                        onPointerDown={() => {
                          handlePointerDown(rowNum, colNum);
                          playStart();
                        }}
                        onTouchMove={(event) => {
                          const touch = event.touches[0];
                          handleTouchEvent(touch);
                        }}
                        onMouseEnter={() => {
                          if (!isPressed) {
                            return;
                          }

                          handleSelectGridCell(rowNum, colNum);
                        }}
                        onKeyDown={(event) =>
                          handleKeyDown(event, rowNum, colNum)
                        }
                      />
                    );
                  })
                )}
              </InteractiveGrid>
            </InteractiveWrapper>
          </InteractiveLayer>
          <ChildLayer>
            <ChildWrapper>
              {hasChild && (
                <GridChild
                  layout={false}
                  style={{
                    gridRow,
                    gridColumn: gridCol,
                    opacity: isPressed ? 0.5 : 0.9,
                  }}
                />
              )}
            </ChildWrapper>
          </ChildLayer>
        </DemoWrapper>
      </Row>
      {showHelpScreen && (
        <HelpScreen
          style={{ gridArea: 'main-content' }}
          handleDismiss={handleDismissHelpScreen}
        />
      )}
    </OuterWrapper>
  );
}

const getButtonId = (id, rowNum, colNum) =>
  `button-${id.replace(/:/g, '')}-${rowNum}-${colNum}`;

const OuterWrapper = styled(Space)`
  display: grid;
  grid-template-areas:
    'header'
    'main-content';
  margin-left: -32px;
  margin-right: -32px;
  margin-bottom: 64px;
  padding: 32px;
  border-radius: 0px;
  background: var(--syntax-bg);
  outline: 1px solid var(--color-content-outline);

  @media ${BREAKPOINTS.mdAndLarger} {
    border-radius: 6px;
  }
`;

const Header = styled.header`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
`;

const Button = styled.button`
  height: 48px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    border: 1px solid var(--color-gray-500);
  }
`;

const SquareButton = styled(Button)`
  display: grid;
  place-content: center;
  width: 48px;
  min-height: 48px;

  &:focus {
    outline-offset: 4px;
  }
`;
const LongButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding: 0px 12px;

  &:focus {
    outline-offset: 4px;
  }
`;

const Row = styled.div`
  grid-area: main-content;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  transition: opacity 400ms;

  @media (max-width: 750px) {
    flex-direction: column-reverse;
    align-items: center;
    justify-content: flex-start;
  }
`;

const DemoWrapper = styled.div`
  position: relative;
  width: 320px;
  aspect-ratio: 1 / 1;
  /* There's a drag gesture here. Prevent standard behaviour */
  user-select: none;

  @media (max-width: 750px) {
    width: 100%;
    max-width: 400px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: repeat(${NUM_OF_ROWS}, 1fr);
  grid-template-columns: repeat(${NUM_OF_COLS}, 1fr);
`;

const ChildLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Don't block clicks */
  pointer-events: none;
`;

const GridChild = styled(BaseItem)``;

const InteractiveLayer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const InteractiveWrapper = styled.div`
  position: absolute;
  inset: 0px;
  overflow: hidden;
`;

const InteractiveGrid = styled(Grid)`
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  touch-action: none;
`;

const GridButton = styled.button`
  position: relative;
  border-right: 2px dashed var(--color-gray-500);
  border-bottom: 2px dashed var(--color-gray-500);
  /*
    The pseudo-element grows by 1.5x, which expands the click target.
    As a result, we sometimes intend to click 1 button, but it
    actually clicks the button in the subsequent row. Fix this by
    truncating overflow.
  */
  overflow: hidden;

  &:focus {
    outline-offset: 0px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 2px;
    background: radial-gradient(
      circle at 50% 50%,
      hsl(0deg 0% 100% / 1) 0%,
      hsl(0deg 0% 100% / 0.987) 4.1%,
      hsl(0deg 0% 100% / 0.896) 11.25%,
      hsl(0deg 0% 100% / 0.741) 17.5%,
      hsl(0deg 0% 100% / 0.55) 23.5%,
      hsl(0deg 0% 100% / 0.352) 29.4%,
      hsl(0deg 0% 100% / 0.175) 35.5%,
      hsl(0deg 0% 100% / 0.049) 42.25%,
      hsl(0deg 0% 100% / 0) 50%
    );
    opacity: 0;
    transition: all 500ms;
  }
  @media (pointer: fine) {
    &:hover::after {
      opacity: 0.6;
      transform: scale(1.5);
      transition: all 200ms;
    }
  }

  &:focus-visible::after {
    opacity: 0.6;
    transform: scale(1.5);
    transition: all 200ms;
  }
`;

const ChildWrapper = styled(Grid)`
  width: 100%;
  height: 100%;
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  min-height: 190px;
  width: 310px;
  /* Override the 'center' alignment in BaseCodeWrapper */
  align-self: flex-start;

  @media (max-width: 750px) {
    flex: unset;
    align-self: center;
    width: 100%;
    max-width: 400px;
  }
`;

export default AssignableChildren;
